<template>
  <div class="max-w-7xl mx-auto pb-10 relative space-y-6">

    <!-- Encabezado -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showCreateModal || showQrModal }">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <Smartphone class="w-6 h-6 text-emerald-500" />
          Conexiones WhatsApp
        </h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">
          Crea instancias y escanea el código QR para conectar tus líneas.
        </p>
      </div>
      <!-- BOTÓN PARA CREAR INSTANCIA (AHORA VALIDA EL PLAN) -->
      <button
        @click="checkPlanAndCreateInstance"
        class="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg shadow-emerald-600/30 transition-all active:scale-95"
      >
        <Plus class="w-5 h-5" /> Nueva Conexión
      </button>
    </div>

    <!-- Grid de Instancias -->
    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <Loader2 class="w-8 h-8 text-emerald-500 animate-spin" />
    </div>

    <div v-else-if="instances.length === 0" class="text-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
      <Smartphone class="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <h3 class="text-lg font-bold text-slate-700 dark:text-slate-300">Sin líneas conectadas</h3>
      <p class="text-sm text-slate-500">Crea una instancia para vincular un número de WhatsApp.</p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" :class="{ 'blur-sm pointer-events-none opacity-50': showCreateModal || showQrModal }">

      <div
        v-for="inst in instances"
        :key="inst.id"
        class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-lg transition-all overflow-hidden flex flex-col relative"
      >
        <!-- Estado Superior -->
        <div class="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center"
             :class="inst.connectionStatus === 'open' ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : 'bg-slate-50/50 dark:bg-slate-800/30'">

          <div class="flex items-center gap-2">
            <span class="relative flex h-3 w-3">
              <span v-if="inst.connectionStatus === 'open'" class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-3 w-3" :class="getStatusColor(inst.connectionStatus)"></span>
            </span>
            <span class="text-xs font-bold uppercase tracking-wider" :class="getTextColor(inst.connectionStatus)">
              {{ getStatusLabel(inst.connectionStatus) }}
            </span>
          </div>

          <button @click="deleteInstance(inst)" class="text-slate-400 hover:text-red-500 transition-colors" title="Eliminar Instancia">
            <Trash2 class="w-4 h-4" />
          </button>
        </div>

        <!-- Info Instancia -->
        <div class="p-5 flex-1 flex flex-col">
          <div class="flex items-center gap-4 mb-4">
            <img
              v-if="inst.profilePicUrl"
              :src="inst.profilePicUrl"
              class="w-12 h-12 rounded-full border border-slate-200 dark:border-slate-700 object-cover shadow-sm"
            />
            <div v-else class="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 shadow-sm">
              <User class="w-6 h-6" />
            </div>

            <div class="flex-1 overflow-hidden">
              <h3 class="text-lg font-bold text-slate-900 dark:text-white truncate">
                {{ inst.profileName || inst.name }}
              </h3>
              <p class="text-xs font-mono text-slate-500 truncate">
                {{ inst.ownerJid ? '+' + inst.ownerJid.split('@')[0] : 'Sin número vinculado' }}
              </p>
            </div>
          </div>

          <!-- Panel de Estadísticas -->
          <div v-if="inst.connectionStatus === 'open'" class="grid grid-cols-3 gap-2 mt-auto mb-2">
            <div class="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-center border border-slate-100 dark:border-slate-700/50">
              <p class="text-[9px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Mensajes</p>
              <p class="text-sm font-bold text-indigo-600 dark:text-indigo-400">{{ inst.stats.Message.toLocaleString() }}</p>
            </div>
            <div class="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-center border border-slate-100 dark:border-slate-700/50">
              <p class="text-[9px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Contactos</p>
              <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">{{ inst.stats.Contact.toLocaleString() }}</p>
            </div>
            <div class="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-center border border-slate-100 dark:border-slate-700/50">
              <p class="text-[9px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Chats</p>
              <p class="text-sm font-bold text-purple-600 dark:text-purple-400">{{ inst.stats.Chat.toLocaleString() }}</p>
            </div>
          </div>

          <div v-else class="mt-auto mb-2 py-3 px-2 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
             <p class="text-xs text-slate-400">Las métricas aparecerán al escanear el QR</p>
          </div>

          <!-- Acciones QR / Logout / Restart -->
          <div class="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex gap-2">

            <button
              v-if="inst.connectionStatus !== 'open'"
              @click="getQrCode(inst.name)"
              class="w-full flex items-center justify-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl transition-colors"
            >
              <QrCode class="w-4 h-4" /> Conectar (Ver QR)
            </button>

            <!-- Botones divididos si está conectado: Cerrar Sesión y Reiniciar -->
            <template v-else>
              <button
                @click="logoutInstance(inst.name)"
                class="flex-1 flex items-center justify-center gap-2 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 text-xs font-bold rounded-xl transition-colors"
              >
                <LogOut class="w-4 h-4" /> Cerrar Sesión
              </button>
              <button
                @click="restartInstance(inst.name)"
                title="Reiniciar conexión en el servidor"
                class="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl transition-colors flex items-center justify-center"
              >
                <RefreshCcw class="w-4 h-4" />
              </button>
            </template>

          </div>
        </div>
      </div>

    </div>

    <!-- Modal: Crear Instancia -->
    <InstanceForm
      :show="showCreateModal"
      @close="showCreateModal = false"
      @created="loadData"
    />

    <!-- Modal: Escanear QR -->
    <div v-if="showQrModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in">
      <div class="bg-white dark:bg-slate-900 w-full max-w-sm rounded-2xl shadow-2xl p-6 text-center border border-slate-200 dark:border-slate-800 relative">
        <button @click="closeQrModal" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-white">
          <X class="w-5 h-5" />
        </button>

        <h3 class="text-lg font-bold text-slate-900 dark:text-white mb-2">Escanea el Código QR</h3>
        <p class="text-xs text-slate-500 mb-6">Abre WhatsApp > Dispositivos Vinculados > Vincular un dispositivo.</p>

        <div class="bg-white p-4 rounded-xl inline-block border border-slate-200">
          <img v-if="qrBase64" :src="qrBase64" alt="QR Code" class="w-48 h-48 object-contain" />
          <div v-else class="w-48 h-48 flex items-center justify-center">
            <Loader2 class="w-8 h-8 text-emerald-500 animate-spin" />
          </div>
        </div>

        <p class="text-[10px] text-slate-400 mt-4 uppercase tracking-widest font-bold">Instancia: {{ currentQrInstance }}</p>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Plus, Smartphone, Trash2, QrCode, LogOut, User, Loader2, X, RefreshCcw } from 'lucide-vue-next';
import axios from 'axios';
import apiClient from '@/api/client';
import Swal from 'sweetalert2';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';
import InstanceForm from './Form.vue';

const EVO_URL = 'https://wapi.therealdevs.pro';
const EVO_KEY = 'TheRealDevsPro2026';

const instances = ref([]);
const currentPlan = ref(null); // <-- Almacena límites de membresía
const isLoading = ref(true);
const showCreateModal = ref(false);
const showQrModal = ref(false);
const qrBase64 = ref(null);
const currentQrInstance = ref('');

const { showSuccess, showError } = useToast();

const getHeaders = () => ({ headers: { apikey: EVO_KEY } });

const loadData = async () => {
  isLoading.value = true;
  try {
    const currentUser = SecureStorage.getItem('neurals_user');
    const isAdmin = currentUser?.role_id !== 3; // true si es Admin
    const queryParams = {};

    if (!isAdmin) {
      queryParams.owner = currentUser.id;
      const planId = currentUser.is_premium > 0 ? currentUser.is_premium : 1;
      const resPlan = await apiClient.get(`/plans/${planId}`);
      currentPlan.value = resPlan.data;
    }

    // 1. Instancias desde MySQL (filtradas por backend según el rol)
    const resDb = await apiClient.get('/instances', { params: queryParams });
    const dbInstances = Array.isArray(resDb.data) ? resDb.data : (resDb.data.data || []);

    // 2. Estrategia según Rol
    if (isAdmin) {
      // ADMIN: Trae todas las instancias de golpe
      const resEvo = await axios.get(`${EVO_URL}/instance/fetchInstances`, getHeaders());
      const allEvo = resEvo.data || [];

      instances.value = dbInstances.map(dbInst => {
        const evo = allEvo.find(e => e.name === dbInst.name) || {};
        return {
          id: dbInst.id,
          name: dbInst.name,
          connectionStatus: evo.connectionStatus || 'close',
          ownerJid: evo.ownerJid || null,
          profilePicUrl: evo.profilePicUrl || null,
          profileName: evo.profileName || null,
          createdAt: evo.createdAt || dbInst.created_at || null,
          stats: evo._count || { Message: 0, Contact: 0, Chat: 0 }
        };
      });

    } else {
      // CLIENTE NORMAL: Consulta individual a Evolution API por cada instancia propia
      const promises = dbInstances.map(async (dbInst) => {
        let evo = {};
        try {
          // Filtrado por nombre de instancia individual en Evolution API
          const evoRes = await axios.get(`${EVO_URL}/instance/fetchInstances?instanceName=${dbInst.name}`, getHeaders());
          if (Array.isArray(evoRes.data) && evoRes.data.length > 0) {
            evo = evoRes.data[0];
          } else if (evoRes.data?.name) {
            evo = evoRes.data;
          }
        } catch {
          evo = {};
        }

        return {
          id: dbInst.id,
          name: dbInst.name,
          connectionStatus: evo.connectionStatus || 'close',
          ownerJid: evo.ownerJid || null,
          profilePicUrl: evo.profilePicUrl || null,
          profileName: evo.profileName || null,
          createdAt: evo.createdAt || dbInst.created_at || null,
          stats: evo._count || { Message: 0, Contact: 0, Chat: 0 }
        };
      });

      instances.value = await Promise.all(promises);
    }

  } catch (e) {
    showError('Error al cargar y sincronizar instancias');
  } finally {
    isLoading.value = false;
  }
};
onMounted(loadData);

// VALIDACIÓN DE NEGOCIO: ¿Puede crear una nueva Instancia?
const checkPlanAndCreateInstance = () => {
  if (currentPlan.value && instances.value.length >= currentPlan.value.max_instances) {
    Swal.fire({
      icon: 'info',
      title: 'Límite Alcanzado',
      text: `Tu membresía (${currentPlan.value.name}) te permite un máximo de ${currentPlan.value.max_instances} conexiones de WhatsApp.`,
      confirmButtonText: 'Ver Planes Superiores',
      confirmButtonColor: '#059669', // Emerald 600
      showCancelButton: true,
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Redirigir a sección de facturación
        // router.push('/billing');
      }
    });
    return;
  }

  showCreateModal.value = true;
};

const getQrCode = async (instanceName) => {
  currentQrInstance.value = instanceName;
  qrBase64.value = null;
  showQrModal.value = true;

  try {
    const res = await axios.get(`${EVO_URL}/instance/connect/${instanceName}`, getHeaders());
    if (res.data?.base64) {
      qrBase64.value = res.data.base64;
    } else {
      showError('La instancia ya está conectada o hubo un error.');
      closeQrModal();
      loadData();
    }
  } catch (e) {
    showError('No se pudo generar el QR.');
    closeQrModal();
  }
};

const closeQrModal = () => {
  showQrModal.value = false;
  qrBase64.value = null;
  loadData();
};

const logoutInstance = async (instanceName) => {
  try {
    await axios.delete(`${EVO_URL}/instance/logout/${instanceName}`, getHeaders());
    showSuccess('Sesión cerrada correctamente');
    loadData();
  } catch (e) {
    showError('Error al cerrar sesión de WhatsApp');
  }
};

// NUEVA FUNCIÓN: REINICIAR INSTANCIA
const restartInstance = async (instanceName) => {
  try {
    // Alerta de confirmación opcional, o puedes hacerlo directo para que sea rápido
    await axios.put(`${EVO_URL}/instance/restart/${instanceName}`, {}, getHeaders());
    showSuccess('Instancia reiniciada. Reconectando...');
    setTimeout(loadData, 2000); // Esperar 2 seg a que Evolution responda el reinicio
  } catch (e) {
    showError('Error al reiniciar la conexión en el servidor');
  }
};


// Obtener imagen de perfil de una instancia específica
const fetchInstanceProfilePic = async (instanceName) => {
  try {
    const res = await axios.post(
      `${EVO_URL}/chat/fetchProfilePictureUrl/${instanceName}`,
      { number: instanceName }, // o el número conectado
      { headers: { apikey: EVO_KEY } }
    );
    return res.data?.profilePictureUrl || null;
  } catch (err) {
    return null;
  }
};

const deleteInstance = (inst) => {
  Swal.fire({
    title: `¿Eliminar ${inst.name}?`,
    text: "Se borrará la conexión permanentemente de Evolution API y tu Base de Datos.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await axios.delete(`${EVO_URL}/instance/delete/${inst.name}`, getHeaders());
        await apiClient.delete(`/instances/${inst.id}`);
        showSuccess('Instancia eliminada');
        loadData();
      } catch (e) {
        showError('No se pudo eliminar la instancia en su totalidad');
      }
    }
  });
};

const getStatusColor = (status) => {
  const map = { 'open': 'bg-emerald-500', 'connecting': 'bg-amber-500', 'close': 'bg-slate-400' };
  return map[status] || 'bg-slate-400';
};
const getTextColor = (status) => {
  const map = { 'open': 'text-emerald-700 dark:text-emerald-400', 'connecting': 'text-amber-600', 'close': 'text-slate-500' };
  return map[status] || 'text-slate-500';
};
const getStatusLabel = (status) => {
  const map = { 'open': 'Conectado', 'connecting': 'Conectando...', 'close': 'Desconectado' };
  return map[status] || 'Desconocido';
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
</style>